import { useConfig } from '@marine/configs';
import { CommunityIcon, ExternalLinkIcon } from '@marine/icons';
import { MenuProps } from 'antd';
import {
  Briefcase,
  FileLock,
  FileText,
  Headphones,
  HelpCircle,
  Lightbulb,
  Mail,
  MessageCircle,
  TrendingUp,
  User,
} from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import { SuitcaseIcon } from '../../CustomIcons';

const TARGET_BLANK = { target: '_blank', rel: 'noopener' };

export const useMoreMenuItems = () => {
  const { mdcLandingUrl } = useConfig();

  const moreMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'about',
        label: 'About TheBoatDB',
        type: 'group',
        children: [
          {
            key: 'how-it-works',
            icon: <Lightbulb size={20} />,
            label: <Link href="/how-it-works">How it works</Link>,
          },
          {
            key: 'our-story',
            icon: <MessageCircle size={20} />,
            label: <Link href="/our-story">Our story</Link>,
          },
          {
            key: 'careers',
            icon: <Briefcase size={20} />,
            label: <Link href="/careers">Career</Link>,
          },
          {
            key: 'contact-us',
            icon: <Mail size={20} />,
            label: (
              <Link href="mailto:Support@MarineDataCloud.com?subject=Support needed for TheBoatDB">Contact us</Link>
            ),
          },
        ],
      },
      {
        type: 'divider',
      },
      {
        key: 'pricing',
        label: 'Pricing',
        type: 'group',
        children: [
          {
            key: 'pricing-and-plans',
            icon: <Lightbulb size={20} />,
            label: <Link href="/pricing">Pricing and Plans</Link>,
          },
        ],
      },
      {
        type: 'divider',
      },
      {
        key: 'support',
        label: 'Support',
        type: 'group',
        children: [
          {
            key: 'system-status',
            icon: <TrendingUp size={20} />,
            label: (
              <Link href="https://status.marinedatacloud.com/" {...TARGET_BLANK}>
                System status <ExternalLinkIcon />
              </Link>
            ),
          },
          {
            key: 'changelog',
            icon: <User size={20} />,
            label: <Link href="/changelog">Changelog</Link>,
          },
          {
            key: 'faq',
            icon: <HelpCircle size={20} />,
            label: (
              <Link
                href="https://support.marinedatacloud.com/portal/en/kb/theboatdb/frequently-asked-questions-faq"
                {...TARGET_BLANK}
              >
                FAQ <ExternalLinkIcon />
              </Link>
            ),
          },
          {
            key: 'tutorials',
            icon: <FileText size={20} />,
            label: (
              <Link href="https://support.marinedatacloud.com/portal/en/kb/theboatdb" {...TARGET_BLANK}>
                Tutorials <ExternalLinkIcon />
              </Link>
            ),
          },
          {
            key: 'community',
            icon: <CommunityIcon />,
            label: (
              <Link href="https://support.marinedatacloud.com/portal/en/community/theboatdb" {...TARGET_BLANK}>
                Community <ExternalLinkIcon />
              </Link>
            ),
          },
          {
            key: 'support',
            icon: <Headphones size={20} />,
            label: (
              <Link href="https://support.marinedatacloud.com" {...TARGET_BLANK}>
                Support <ExternalLinkIcon />
              </Link>
            ),
          },
        ],
      },
      {
        type: 'divider',
      },
      {
        key: 'legal',
        label: 'Legal',
        type: 'group',
        children: [
          {
            key: 'terms-of-service',
            icon: <SuitcaseIcon />,
            label: <Link href={`${mdcLandingUrl}/terms-of-service`}>Terms of service</Link>,
          },
          {
            key: 'user-agreement',
            icon: <User size={20} />,
            label: <Link href={`${mdcLandingUrl}/user-agreement`}>User agreement</Link>,
          },
          {
            key: 'privacy-policy',
            icon: <FileLock />,
            label: <Link href={`${mdcLandingUrl}/privacy-policy`}>Privacy policy</Link>,
          },
        ],
      },
    ],
    [mdcLandingUrl],
  );

  return moreMenuItems;
};
